import { IS_ENV_DEPLOYED, IS_RENDER_DEPLOYED, IS_VERCEL_DEPLOYED } from "@aix/env"
import {
  clearRequestContextProvider,
  blg as emitBlg,
  flush as emitFlush,
  setRequestContextProvider,
} from "@aix/jsutils/src/axiom-events/emit"
import { createRequestScopedSlot } from "@aix/jsutils/src/axiom-events/requestScope"

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

export interface BackendLoggingContext {
  userId?: string
  userEmail?: string
  reqParams?: any
  reqBody?: any
  requestId: string
  traceId?: string
  spanId?: string
  path?: string

  // Product/app identifier (e.g. "humantext", "artmax"). Surfaced top-level so a single
  // shared Axiom dataset / Sentry project can be split per product. Optional — apps that
  // don't set it are unaffected.
  appId?: string

  // What started this work: `trpc:<router.procedure>` or `inngest:<functionId>`. Surfaced top-level so
  // DB/log rows can be attributed to their caller.
  origin?: string

  // Note: should be used sparingly as this will override the existing misc
  misc?: any
}

export interface RequestContextAccessors {
  getRequestContext: () => BackendLoggingContext
  addToRequestContext: (context: Partial<BackendLoggingContext>) => void
  clearRequestContext: () => void
}

// Current request's accessors: per request inside `withRequestScope` (requestScope.ts), else module-global
const currentAccessors = createRequestScopedSlot<RequestContextAccessors>()

export const createRequestContext = (): RequestContextAccessors => {
  let context: BackendLoggingContext | null = null

  return {
    getRequestContext: () => {
      if (!context) {
        return {} as BackendLoggingContext
      }
      return context
    },
    addToRequestContext: (newContext: Partial<BackendLoggingContext>) => {
      context = {
        ...context,
        ...newContext,
      } as BackendLoggingContext
    },
    clearRequestContext: () => {
      context = null
    },
  }
}

// Global accessor functions that delegate to current request's context
export const getRequestContext = () => {
  const accessors = currentAccessors.get()
  if (!accessors) {
    return {} // Return empty object for non-request contexts (e.g. cron jobs)
  }
  return accessors.getRequestContext()
}

export const addToRequestContext = (context: Partial<BackendLoggingContext>) => {
  currentAccessors.get()?.addToRequestContext(context)
}

export const clearRequestContext = () => {
  currentAccessors.get()?.clearRequestContext()
}

export const setRequestContextAccessors = (accessors: RequestContextAccessors | null) => {
  currentAccessors.set(accessors)

  // Bridge to emit.ts request context so flat-field logs are enriched with userId/requestId
  if (accessors) {
    setRequestContextProvider(() => {
      const ctx = accessors.getRequestContext()
      return {
        userId: ctx.userId,
        traceId: ctx.traceId,
        spanId: ctx.spanId,
        requestId: ctx.requestId,
        appId: ctx.appId,
        origin: ctx.origin,
      }
    })
  } else {
    clearRequestContextProvider()
  }
}

// Function to determine the deployment environment
export const getDeploymentLocation = (): string => {
  if (IS_ENV_DEPLOYED) {
    if (IS_VERCEL_DEPLOYED) {
      return "backend-vercel"
    }
    if (IS_RENDER_DEPLOYED) {
      return "backend-render"
    }
    return "unknown-deployed"
  }
  return "local"
}

export const logFlush = emitFlush
export { setRequestContextProvider, clearRequestContextProvider }

/**
 * Structured event types for reliable Axiom querying
 * Use data.type == "ops" or data.type == "monitor" in APL
 *
 * OPS METRICS vs PRODUCT METRICS:
 * - blg.ops() → Axiom: System health, uptime, throughput (engineering)
 * - PostHog  → Product: User behavior, funnels, conversions (product)
 *
 * Heuristics:
 * - "Would I page on-call if this stops?" → blg.ops()
 * - "Would I show this to a PM/investor?" → PostHog
 * - Revenue events often need BOTH (ops heartbeat + product funnel)
 */
export type StructuredEventType = "ops" | "monitor"

/**
 * blg from emit.ts (@aix/jsutils).
 * Re-exported here for backward compatibility with files that import from "@aix/apiutils/src/logging".
 * For new code, import directly: `import { blg } from "@aix/jsutils"`
 */
export const blg = emitBlg
